"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCode2Outline18, IconEyeOutline18 } from "nucleo-ui-outline-18";
import { Badge } from "@/components/ui/badge";
import { isSolanaAddress } from "@/lib/utils/address";
import JsonEditorBase from "@/app/(v2)/workflows/_components/form-fields/JsonEditorBase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
interface FilterDisplayProps {
  filter: unknown;
  size?: "sm" | "md" | "lg";
  className?: string;
}
interface ParsedCondition {
  field: string;
  operator: string;
  value: string;
  displayOperator: string;
}

/**
 * Parse a filter object into human-readable conditions
 */
function parseFilter(filter: unknown): {
  conditions: ParsedCondition[];
  logicalOp: string;
} | null {
  if (!filter || typeof filter !== "object") return null;
  const filterObj = filter as Record<string, unknown>;
  const conditions: ParsedCondition[] = [];
  let logicalOp = "AND";

  // Handle $and, $or operators
  if (filterObj.$and && Array.isArray(filterObj.$and)) {
    logicalOp = "AND";
    filterObj.$and.forEach((item: unknown) => {
      const parsed = parseSingleCondition(item);
      if (parsed) conditions.push(parsed);
    });
  } else if (filterObj.$or && Array.isArray(filterObj.$or)) {
    logicalOp = "OR";
    filterObj.$or.forEach((item: unknown) => {
      const parsed = parseSingleCondition(item);
      if (parsed) conditions.push(parsed);
    });
  } else {
    // Single condition without logical operator
    const parsed = parseSingleCondition(filterObj);
    if (parsed) conditions.push(parsed);
  }
  return conditions.length > 0 ? {
    conditions,
    logicalOp
  } : null;
}

/**
 * Parse a single condition object
 */
function parseSingleCondition(condition: unknown): ParsedCondition | null {
  if (!condition || typeof condition !== "object") return null;
  const conditionObj = condition as Record<string, unknown>;

  // Get the field name (first key)
  const field = Object.keys(conditionObj)[0];
  if (!field) return null;
  const fieldValue = conditionObj[field];
  if (typeof fieldValue !== "object" || !fieldValue) return null;
  const fieldValueObj = fieldValue as Record<string, unknown>;

  // Get the operator (e.g., $eq, $gte, $lte)
  const operator = Object.keys(fieldValueObj)[0];
  const value = fieldValueObj[operator];

  // Map operator to display format
  const operatorMap: Record<string, string> = {
    $eq: "=",
    $ne: "≠",
    $gt: ">",
    $gte: "≥",
    $lt: "<",
    $lte: "≤",
    $in: "in",
    $nin: "not in"
  };
  return {
    field,
    operator,
    value: String(value),
    displayOperator: operatorMap[operator] || operator
  };
}

/**
 * Format a value for display (truncate addresses, format numbers)
 */
function formatValue(value: string): {
  display: string;
  full: string;
} {
  // Check if it's a blockchain address (EVM or Solana)
  if (value.startsWith("0x") && value.length === 42 || isSolanaAddress(value)) {
    return {
      display: `${value.slice(0, 6)}...${value.slice(-4)}`,
      full: value
    };
  }

  // Check if it's a large number
  if (/^\d{15,}$/.test(value)) {
    // Format with underscores for readability
    const formatted = value.replace(/\B(?=(\d{3})+(?!\d))/g, "_");
    return {
      display: formatted.length > 20 ? `${formatted.slice(0, 20)}...` : formatted,
      full: value
    };
  }

  // Regular value
  return {
    display: value.length > 30 ? `${value.slice(0, 30)}...` : value,
    full: value
  };
}

/**
 * FilterDisplay - Component for displaying EVM log filters
 * Shows parsed conditions as badges with option to view raw JSON
 */
export function FilterDisplay(t0) {
  const $ = _c(69);
  const {
    filter,
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  let T0;
  let T1;
  let filterJson;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== className || $[1] !== filter || $[2] !== size) {
    const parsed = parseFilter(filter);
    let t8;
    if ($[12] !== filter) {
      t8 = typeof filter === "string" ? filter : JSON.stringify(filter, null, 2);
      $[12] = filter;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    filterJson = t8;
    let T2;
    let T3;
    let iconSize;
    let t10;
    let t11;
    let t12;
    let t13;
    let t9;
    let triggerSize;
    if ($[14] !== className || $[15] !== size) {
      const sizeClasses = {
        sm: {
          trigger: "text-3xs px-2 py-0.5 gap-1",
          icon: "h-2.5 w-2.5"
        },
        md: {
          trigger: "text-xs px-2.5 py-1 gap-1.5",
          icon: "h-3 w-3"
        },
        lg: {
          trigger: "text-sm px-3 py-1.5 gap-2",
          icon: "h-3.5 w-3.5"
        }
      };
      ({
        trigger: triggerSize,
        icon: iconSize
      } = sizeClasses[size]);
      T1 = Tabs;
      t5 = "parsed";
      if ($[28] !== className) {
        t6 = cn("space-y-2", className);
        $[28] = className;
        $[29] = t6;
      } else {
        t6 = $[29];
      }
      t12 = "flex items-center justify-between";
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = <span className="table-header">Filter:</span>;
        $[30] = t13;
      } else {
        t13 = $[30];
      }
      T3 = TabsList;
      t11 = "rounded-ds-xs inline-flex h-auto bg-neutral-100 p-0.5 dark:bg-neutral-900";
      T2 = TabsTrigger;
      t9 = "parsed";
      t10 = cn("rounded-ds-5 flex items-center !py-0 font-normal uppercase tracking-wider data-[state=active]:border data-[state=active]:border-black/15 data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-[inset_1px_1px_1px_theme(colors.white),inset_-1px_-1px_1px_theme(colors.neutral.500),2px_2px_4px_rgba(0,0,0,0.15)] dark:data-[state=active]:border-black/30 dark:data-[state=active]:bg-neutral-700 dark:data-[state=active]:text-white dark:data-[state=active]:shadow-[inset_1px_1px_1px_theme(colors.neutral.500),inset_-1px_-1px_1px_theme(colors.neutral.900),2px_2px_4px_rgba(0,0,0,0.8)]", triggerSize);
      $[14] = className;
      $[15] = size;
      $[16] = T1;
      $[17] = T2;
      $[18] = T3;
      $[19] = iconSize;
      $[20] = t10;
      $[21] = t11;
      $[22] = t12;
      $[23] = t13;
      $[24] = t5;
      $[25] = t6;
      $[26] = t9;
      $[27] = triggerSize;
    } else {
      T1 = $[16];
      T2 = $[17];
      T3 = $[18];
      iconSize = $[19];
      t10 = $[20];
      t11 = $[21];
      t12 = $[22];
      t13 = $[23];
      t5 = $[24];
      t6 = $[25];
      t9 = $[26];
      triggerSize = $[27];
    }
    let t14;
    if ($[31] !== iconSize) {
      t14 = <IconEyeOutline18 className={iconSize} />;
      $[31] = iconSize;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] !== T2 || $[34] !== t10 || $[35] !== t14 || $[36] !== t9) {
      t15 = <T2 value={t9} className={t10}>{t14}Parsed</T2>;
      $[33] = T2;
      $[34] = t10;
      $[35] = t14;
      $[36] = t9;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    let t16;
    if ($[38] !== triggerSize) {
      t16 = cn("rounded-ds-5 flex items-center !py-0 font-normal uppercase tracking-wider data-[state=active]:border data-[state=active]:border-black/15 data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-[inset_1px_1px_1px_theme(colors.white),inset_-1px_-1px_1px_theme(colors.neutral.500),2px_2px_4px_rgba(0,0,0,0.15)] dark:data-[state=active]:border-black/30 dark:data-[state=active]:bg-neutral-800 dark:data-[state=active]:text-white dark:data-[state=active]:shadow-[inset_1px_1px_1px_theme(colors.neutral.500),inset_-1px_-1px_1px_theme(colors.neutral.900),2px_2px_4px_rgba(0,0,0,0.8)]", triggerSize);
      $[38] = triggerSize;
      $[39] = t16;
    } else {
      t16 = $[39];
    }
    let t17;
    if ($[40] !== iconSize) {
      t17 = <IconCode2Outline18 className={iconSize} />;
      $[40] = iconSize;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    let t18;
    if ($[42] !== t16 || $[43] !== t17) {
      t18 = <TabsTrigger value="json" className={t16}>{t17}JSON</TabsTrigger>;
      $[42] = t16;
      $[43] = t17;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    let t19;
    if ($[45] !== T3 || $[46] !== t11 || $[47] !== t15 || $[48] !== t18) {
      t19 = <T3 className={t11}>{t15}{t18}</T3>;
      $[45] = T3;
      $[46] = t11;
      $[47] = t15;
      $[48] = t18;
      $[49] = t19;
    } else {
      t19 = $[49];
    }
    if ($[50] !== t12 || $[51] !== t13 || $[52] !== t19) {
      t7 = <div className={t12}>{t13}{t19}</div>;
      $[50] = t12;
      $[51] = t13;
      $[52] = t19;
      $[53] = t7;
    } else {
      t7 = $[53];
    }
    T0 = TabsContent;
    t2 = "parsed";
    t3 = "mt-0";
    t4 = parsed ? <div className="space-y-2">{parsed.conditions.length > 1 && <Badge variant="purple">{parsed.logicalOp}</Badge>}<div className="flex flex-wrap gap-2">{parsed.conditions.map(_temp)}</div></div> : <p className="label-xs">No filter conditions</p>;
    $[0] = className;
    $[1] = filter;
    $[2] = size;
    $[3] = T0;
    $[4] = T1;
    $[5] = filterJson;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    T0 = $[3];
    T1 = $[4];
    filterJson = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[54] !== T0 || $[55] !== t2 || $[56] !== t3 || $[57] !== t4) {
    t8 = <T0 value={t2} className={t3}>{t4}</T0>;
    $[54] = T0;
    $[55] = t2;
    $[56] = t3;
    $[57] = t4;
    $[58] = t8;
  } else {
    t8 = $[58];
  }
  let t9;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      valid: true
    };
    $[59] = t9;
  } else {
    t9 = $[59];
  }
  let t10;
  if ($[60] !== filterJson) {
    t10 = <TabsContent value="json" className="mt-0"><div className="pointer-events-none [&_.space-y-2]:space-y-0"><JsonEditorBase label="" value={filterJson} onChange={_temp2} placeholder="{}" minHeight="120px" validation={t9} /></div></TabsContent>;
    $[60] = filterJson;
    $[61] = t10;
  } else {
    t10 = $[61];
  }
  let t11;
  if ($[62] !== T1 || $[63] !== t10 || $[64] !== t5 || $[65] !== t6 || $[66] !== t7 || $[67] !== t8) {
    t11 = <T1 defaultValue={t5} className={t6}>{t7}{t8}{t10}</T1>;
    $[62] = T1;
    $[63] = t10;
    $[64] = t5;
    $[65] = t6;
    $[66] = t7;
    $[67] = t8;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  return t11;
}
function _temp2() {}
function _temp(condition, idx) {
  const formattedValue = formatValue(condition.value);
  return <div key={idx} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 dark:border-blue-800 dark:bg-blue-950/30" title={`${condition.field} ${condition.displayOperator} ${formattedValue.full}`}><span className="font-mono text-xs font-semibold text-blue-900 dark:text-blue-100">{condition.field}</span><span className="text-xs font-medium text-blue-700 dark:text-blue-300">{condition.displayOperator}</span><span className="font-mono text-xs text-blue-700 dark:text-blue-300">{formattedValue.display}</span></div>;
}